import { memo, useLayoutEffect } from "react";
import { useViewTransitionState } from "react-router-dom";
import { createGlobalStyle, css } from "styled-components";

import { getViewTransitionAnimationName } from "../../utils/functions";

export type ViewTransitionItem = {
   name: string;
   path: string;
   css: ReturnType<typeof css>;
   /** @default 0.2s */
   animationDuration?: number;
};

const defaultAnimationDuration = 0.2;

function getViewTransitionHtmlClassName(viewTransitionName: ViewTransitionItem["name"]) {
   return `${viewTransitionName}-view-transition`;
}

const Styles = createGlobalStyle<{
   item: ViewTransitionItem;
}>`
   .${(props) => props.item.name} {
      view-transition-name: ${(props) => props.item.name};
   }

   ::view-transition-new(${(props) => props.item.name}) {
      animation: ${(props) => getViewTransitionAnimationName(props.item.name)} ${(props) => props.item.animationDuration ?? defaultAnimationDuration}s ease forwards;
   }
   ::view-transition-old(${(props) => props.item.name}) {
      animation: ${(props) => getViewTransitionAnimationName(props.item.name)} ${(props) => (props.item.animationDuration ?? defaultAnimationDuration) * 0.7}s ease forwards reverse;
   }

   html.${(props) => getViewTransitionHtmlClassName(props.item.name)}::view-transition-old(root) {
      animation: viewTransitionFadeOutAnimation ${(props) => props.item.animationDuration ?? defaultAnimationDuration}s ease both;
      mix-blend-mode: plus-lighter;
   }
   html.${(props) => getViewTransitionHtmlClassName(props.item.name)}::view-transition-new(root) {
      animation: viewTransitionFadeInAnimation ${(props) => props.item.animationDuration ?? defaultAnimationDuration}s ease both;
      mix-blend-mode: plus-lighter;
   }

   ${(props) => props.item.css}
`;

type SingleViewTransitionProps = {
   item: ViewTransitionItem;
};

function SingleViewTransition({ item }: SingleViewTransitionProps) {
   const isViewTransitionActive = useViewTransitionState(item.path);

   const className = getViewTransitionHtmlClassName(item.name);

   useLayoutEffect(() => {
      if (!isViewTransitionActive) return;

      document.documentElement.classList.add(className);

      return () => {
         document.documentElement.classList.remove(className);
      };
   }, [className, isViewTransitionActive]);

   return (
      <>
         <Styles item={item} />
      </>
   );
}

export default memo(SingleViewTransition);
